"use client";

import React, { useMemo, useState, useCallback } from "react";
import { Input, Button } from "antd";
import AppLayoutDoctor from "../../../navigation/Layout/LayoutDoctor";
import searchImg from "../../../assets/icons/search.png";
import { useTranslation } from "react-i18next";
import "./Medications.css";



/* =========================
   Fake Data
========================= */
const MEDICATIONS_FAKE = [
  {
    id: "med-1",
    name: "Dolirhume",
    form: "Tablet",
    imageUrl: "https://via.placeholder.com/44x44.png?text=D",
  },
  {
    id: "med-2",
    name: "Doliprane",
    form: "Capsule",
    imageUrl: "https://via.placeholder.com/44x44.png?text=DP",
  },
  {
    id: "med-3",
    name: "Dovephin",
    form: "Tablet",
    imageUrl: "https://via.placeholder.com/44x44.png?text=DV",
  },
  {
    id: "med-4",
    name: "Dory",
    form: "Tablet",
    imageUrl: "https://via.placeholder.com/44x44.png?text=DR",
  },
  {
    id: "med-5",
    name: "",
    form: "Tablet",
    imageUrl: "",
  },
  {
    id: "med-6",
    form: "Tablet",
    imageUrl: "",
  },
];

/* =========================
   Helpers (safe UI)
========================= */
const safeText = (v, fallback = "—") =>
  v === null || v === undefined || String(v).trim() === "" ? fallback : String(v);

const safeForm = (m, fallback) => safeText(m?.form, fallback);
const DEFAULT_MED_AVATAR = `data:image/svg+xml;utf8,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M10.5 20.5L20.5 10.5C20.9673 10.0421 21.3391 9.49606 21.5941 8.89352C21.849 8.29099 21.982 7.64389 21.9853 6.98965C21.9886 6.33541 21.8622 5.687 21.6133 5.08192C21.3645 4.47684 20.9982 3.92709 20.5355 3.46447C20.0729 3.00184 19.5232 2.63552 18.9181 2.38668C18.313 2.13783 17.6646 2.01141 17.0104 2.01471C16.3561 2.01802 15.709 2.15098 15.1065 2.40593C14.5039 2.66087 13.9579 3.03273 13.5 3.5L3.5 13.5C3.03273 13.9579 2.66087 14.5039 2.40593 15.1065C2.15098 15.709 2.01802 16.3561 2.01471 17.0104C2.01141 17.6646 2.13783 18.313 2.38668 18.9181C2.63552 19.5232 3.00184 20.0729 3.46447 20.5355C3.92709 20.9982 4.47684 21.3645 5.08192 21.6133C5.687 21.8622 6.33541 21.9886 6.98965 21.9853C7.64389 21.982 8.29099 21.849 8.89352 21.5941C9.49606 21.3391 10.0421 20.9673 10.5 20.5Z" stroke="#1e1e1e" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/><path d="M8.5 8.5L15.5 15.5" stroke="#1e1e1e" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg>')}`;

const safeImg = (m) => {
  const url = m?.imageUrl;
  if (!url || String(url).trim() === "" || !url.startsWith("http")) {
    return DEFAULT_MED_AVATAR;
  }
  return url;
};

/* =========================
   Helpers (search normalize)
========================= */
const normalize = (s) =>
  String(s || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const hasRealName = (m) =>
  m?.name !== null && m?.name !== undefined && String(m.name).trim() !== "";

export default function MedicationsDoctor() {
  const { t } = useTranslation();

  const [query, setQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  const [results, setResults] = useState(MEDICATIONS_FAKE);
  const [hasTyped, setHasTyped] = useState(false);

  const resetToFullList = useCallback(() => {
    setResults(MEDICATIONS_FAKE);
    setHasTyped(false);
  }, []);

  const safeName = (m) => safeText(m?.name, t("meds.unknownMedication"));

  const handleQueryChange = (e) => {
    const val = e.target.value;
    setQuery(val);

    const q = val.trim();
    if (q === "") {
      resetToFullList();
      return;
    }

    setHasTyped(true);

    const qNorm = normalize(q);

    const found = MEDICATIONS_FAKE.filter(hasRealName).filter((m) =>
      normalize(m.name).includes(qNorm)
    );

    setResults(found);
  };

  const filtered = useMemo(() => results, [results]);
  const showEmpty = hasTyped && filtered.length === 0;

  const canSearch = useMemo(() => query.trim().length > 0, [query]);
  const handleSearch = async () => {
    const q = query.trim();
    if (!q) return;

    try {
      setIsSearching(true);
      await new Promise((r) => setTimeout(r, 150));
    } finally {
      setIsSearching(false);
    }
  };

  const handleUseMedication = (med) => {
  };

  const handleViewNotice = (med) => {
  };

  return (
    <AppLayoutDoctor>
      <div className="meds-page">
        <div className="meds-card">
          <div className="meds-head">
            <h2 className="meds-title">{t("meds.title")}</h2>
            <p className="meds-subtitle">{t("meds.subtitle")}</p>
          </div>

          <div className="meds-search">
            <Input
              className="meds-input"
              placeholder={t("meds.inputPlaceholder")}
              value={query}
              onChange={handleQueryChange}
              allowClear
            />

            <Button
              className="meds-btn"
              type="primary"
              loading={isSearching}
              disabled={!canSearch}
              onClick={handleSearch}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  d="M17.5003 17.5003L13.762 13.7553M15.8337 8.75033C15.8337 10.6289 15.0874 12.4306 13.759 13.759C12.4306 15.0874 10.6289 15.8337 8.75033 15.8337C6.87171 15.8337 5.07004 15.0874 3.74165 13.759C2.41327 12.4306 1.66699 10.6289 1.66699 8.75033C1.66699 6.87171 2.41327 5.07004 3.74165 3.74165C5.07004 2.41327 6.87171 1.66699 8.75033 1.66699C10.6289 1.66699 12.4306 2.41327 13.759 3.74165C15.0874 5.07004 15.8337 6.87171 15.8337 8.75033Z"
                  stroke="white"
                  strokeWidth="1.66667"
                  strokeLinecap="round"
                />
              </svg>
              {t("meds.search")}
            </Button>
          </div>

          {showEmpty ? (
            <div className="meds-body">
              <EmptyIllustration
                searchImg={searchImg}
                alt={t("meds.emptyAlt")}
              />
            </div>
          ) : (
            <div className="meds-listWrap">
              <div className="meds-list">
                {filtered.map((m, idx) => (
                  <div className="meds-row" key={m.id || idx}>
                    <div className="meds-leftt">
                      <img
                        className="meds-img"
                        src={safeImg(m)}
                        alt={safeName(m)}
                        loading="lazy"
                      />
                      <div className="meds-name">{safeName(m)}</div>
                    </div>

                    <div className="meds-form">
                      {safeForm(m, t("meds.unknownForm"))}
                    </div>

                    <div className="meds-actions">
                   

                      <button
                        className="meds-noticeBtn"
                        type="button"
                        onClick={() => handleViewNotice(m)}
                      >
                        {t("meds.viewNotice")}
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                        >
                          <path
                            d="M9.99935 3.33366C13.6752 3.33366 16.666 6.32449 16.666 10.0003C16.666 13.6762 13.6752 16.667 9.99935 16.667C6.32352 16.667 3.33268 13.6762 3.33268 10.0003C3.33268 6.32449 6.32352 3.33366 9.99935 3.33366ZM9.99935 1.66699C5.39685 1.66699 1.66602 5.39783 1.66602 10.0003C1.66602 14.6028 5.39685 18.3337 9.99935 18.3337C14.6018 18.3337 18.3327 14.6028 18.3327 10.0003C18.3327 5.39783 14.6018 1.66699 9.99935 1.66699ZM10.8327 12.5003H9.16602V14.167H10.8327V12.5003ZM9.16602 10.8337H10.8327L11.2493 5.83366H8.74935L9.16602 10.8337Z"
                            fill="#1e1e1e"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </AppLayoutDoctor>
  );
}

/* =========================
   Empty illustration
========================= */
function EmptyIllustration({ searchImg, alt }) {
  return (
    <div className="meds-empty">
      <img
        src={searchImg}
        alt={alt}
        className="meds-empty__svg"
        loading="lazy"
      />
    </div>
  );
}
